/**
 * Desktop release-notification subscriptions, served by customer-service behind
 * the API gateway (CUS-721 — migrated off the standalone desktop-release-notifier
 * service). Uses the shared gateway transport, so the bearer header and 401
 * refresh-and-retry come from the same interceptor stack as every other gateway
 * call — no per-request token handling here.
 *
 * Contract (customer-service /customers/v1/desktop-notifications):
 *   GET    /desktop-notifications              -> { subscriptions: [...] }
 *   PUT    /desktop-notifications  { partnerId, channel, endpoint, protocol }
 *   DELETE /desktop-notifications  { partnerId, channel }
 *
 * Note the casing split the service's OpenAPI contract dictates: request bodies
 * are camelCase (partnerId), responses are snake_case (partner_id).
 */
import { getGatewayTransport } from '~/apis/gatewayTransport'

import type { TransportResponse } from '@mangovoice/api-client'

// 'suppressed' is set by customer-service's Mailgun webhook (CUS-791) when a
// provider-side event kills the row (a complaint, permanent bounce, or an
// unsubscribe via Mailgun's own footer link), as opposed to 'unsubscribed',
// which the in-app toggle and our one-click link set. Both are "opted out" for
// the UI's purposes.
export type ReleaseSubscriptionStatus =
  'confirmed' | 'pending' | 'unsubscribed' | 'suppressed'

export interface ReleaseSubscription {
  user_id: string
  partner_id: string
  channel: string
  endpoint: string
  protocol: string
  status: ReleaseSubscriptionStatus
  created_at: string
  confirmed_at?: string
  updated_at?: string
}

export interface ListSubscriptionsResponse {
  subscriptions: ReleaseSubscription[]
}

export interface SubscribePayload {
  partnerId: string
  channel: string
  endpoint: string
  /** v1 supports only 'email'; the service rejects others. */
  protocol: 'email'
}

export interface UnsubscribePayload {
  partnerId: string
  channel: string
}

const DESKTOP_NOTIFICATIONS_PATH = '/customers/v1/desktop-notifications'

export const desktopNotificationsApi = {
  listSubscriptions: (): Promise<
    TransportResponse<ListSubscriptionsResponse>
  > => getGatewayTransport().get(DESKTOP_NOTIFICATIONS_PATH),

  subscribe: (
    payload: SubscribePayload,
  ): Promise<TransportResponse<ReleaseSubscription>> =>
    getGatewayTransport().put(DESKTOP_NOTIFICATIONS_PATH, payload),

  unsubscribe: (
    payload: UnsubscribePayload,
  ): Promise<TransportResponse<{ ok: boolean }>> =>
    getGatewayTransport().delete(DESKTOP_NOTIFICATIONS_PATH, undefined, {
      data: payload,
    }),
}
